import { router } from "@inertiajs/react"
import { Bell } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { t } from "@/lib/i18n"
import { formatRelativeTime } from "@/lib/time"
import type { NotificationData } from "@/types"

interface Props {
  unreadCount: number
  notifications: NotificationData[]
}

export function NotificationBell({ unreadCount, notifications }: Props) {
  const [open, setOpen] = useState(false)

  function handleMarkAllRead() {
    router.post("/notificaciones/mark_all_read")
    setOpen(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="hover:bg-krown-700 relative h-[34px] w-[34px] text-white"
          aria-label="Notificaciones"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="bg-krown-accent text-krown-800 absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">{t.notifications.title}</span>
          {unreadCount > 0 && (
            <button
              className="text-krown-400 text-xs hover:underline"
              onClick={handleMarkAllRead}
            >
              {t.notifications.markAllRead}
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map((n) => (
              <div
                key={n.id}
                data-unread={!n.read_at ? "true" : "false"}
                className={`hover:bg-accent flex cursor-pointer gap-3 px-3 py-2 ${
                  !n.read_at
                    ? "border-l-krown-300 bg-krown-50/30 border-l-2"
                    : ""
                }`}
                onClick={() => {
                  setOpen(false)
                  router.visit("/notificaciones")
                }}
              >
                <div className="bg-krown-100 text-krown-600 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                  {n.actor.first_name[0]}
                  {n.actor.last_name[0]}
                </div>
                <div className="flex-1">
                  <p className={`text-sm ${!n.read_at ? "font-semibold" : ""}`}>
                    <span>{n.actor.full_name}</span>{" "}
                    <span className="text-neutral-500">
                      {t.notifications.actions[n.action] ?? n.action}
                    </span>
                  </p>
                  <p className="text-xs text-neutral-400">
                    {formatRelativeTime(n.created_at)}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-6 text-center text-sm text-neutral-400">
              {t.notifications.noNotifications}
            </div>
          )}
        </div>
        {notifications.length > 0 && (
          <div className="border-t px-3 py-2 text-center">
            <button
              className="text-krown-400 text-xs hover:underline"
              onClick={() => {
                setOpen(false)
                router.visit("/notificaciones")
              }}
            >
              {t.notifications.viewAll}
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
